import { useEffect, useState } from 'react';
import { api, type SystemMetricsDTO } from './api';
import './system-metrics.css';

function percentage(value: number | null | undefined): string {
  return value == null ? '暂无数据' : `${value.toFixed(1)}%`;
}
function rate(value: number | null | undefined): string {
  if (value == null) return '暂无数据';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} MB/s`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)} KB/s`;
  return `${value.toFixed(0)} B/s`;
}

export function SystemMetricsPanel({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<SystemMetricsDTO | null>(null);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    let active = true, pending = false;
    const load = async (forceRefresh = false) => {
      if (pending) return;
      pending = true;
      setRefreshing(true);
      try {
        const value = await api.getSystemMetrics(forceRefresh);
        if (active) { setData(value); setFailed(false); }
      } catch { if (active) setFailed(true); }
      finally { pending = false; if (active) setRefreshing(false); }
    };
    void load(refreshKey > 0);
    const timer = window.setInterval(() => load(), 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [refreshKey]);

  const issue = failed ? (data?.sampledAt ? '资源刷新失败，当前显示上次采样数据' : '服务器资源暂不可用，请稍后刷新') : data?.message;
  return <section className="card card-pad system-metrics" aria-labelledby="system-metrics-title">
    <div className="system-metrics-heading"><h2 id="system-metrics-title">服务器资源</h2><span role="status">{refreshing ? '正在刷新…' : '每 30 秒刷新'}</span></div>
    {!data && !failed ? <p role="status" className="system-metrics-status">正在采集服务器资源…</p> : <>
      <dl className="system-metrics-grid">
        <div><dt>CPU 使用率</dt><dd>{percentage(data?.cpuPercent)}</dd><span className="system-metrics-caption">系统 CPU 占用</span></div>
        <div><dt>内存使用率</dt><dd>{percentage(data?.memoryPercent)}</dd><span className="system-metrics-caption">不含可回收缓存</span></div>
        <div><dt>磁盘使用率</dt><dd>{percentage(data?.diskPercent)}</dd><span className="system-metrics-caption">主磁盘空间</span></div>
        <div><dt>系统负载</dt><dd className="system-metrics-load">{(data?.loadAverage ?? [null, null, null]).map((value, index) => <span key={index}>{value === null ? '—' : value.toFixed(2)}</span>)}</dd><span className="system-metrics-caption">1 / 5 / 15 分钟{data?.loadAverage.every(value => value === null) ? ' · 暂无数据' : ''}</span></div>
        <div><dt>公网下载</dt><dd>{rate(data?.networkRxBytesPerSecond)}</dd><span className="system-metrics-caption">宿主机 eth0 接收</span></div>
        <div><dt>公网上传</dt><dd>{rate(data?.networkTxBytesPerSecond)}</dd><span className="system-metrics-caption">宿主机 eth0 发送</span></div>
      </dl>
      <div className="system-metrics-footer" role="status">
        {issue && <span className="system-metrics-issue">{issue}</span>}
        {data?.sampledAt && <span>采样时间 {new Date(data.sampledAt).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</span>}
      </div>
    </>}
  </section>;
}
